import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Button, Badge } from 'reactstrap';
import { FaRegCalendarDays } from 'react-icons/fa6';
import { IoChevronDownOutline } from 'react-icons/io5';
import { EmployeeInitialsDropdown } from '../../../core/components/EmployeeInitials';
import DropdownMenuPortal from '../../tasks/WorkOrders/QuickTasks/components/DropdownMenuPortal';
import {
  renderStatusPill,
  renderPriorityPill,
} from '../../tasks/WorkOrders/WorkOrderTable/constants';
import {
  useSetOccurrenceStatusByIdMutation,
  useUpdateOccurrenceMutation,
  TaskOccurrence,
} from '../../../core/api/rtk/taskTemplatesApi';
import { selectEmployees } from '../../employees/reducer';
import { apiError } from '../../../core/toastMessages';

interface TaskSource {
  id: number;
  name: string;
  kind: 'group' | 'item';
}

interface MappedProject {
  id: string;
  name: string;
  status?: string;
  raw?: TaskOccurrence | null;
  source?: TaskSource | null;
}

type SourceSelectHandler = (
  source: { id: number; kind: 'group' | 'item' } | null | undefined
) => void;

interface ProjectsSectionProps {
  projects: MappedProject[];
  onAdd?: () => void;
  onOpenOccurrence?: (occurrence: TaskOccurrence) => void;
  onArchive?: (project: MappedProject) => void;
  showSource?: boolean;
  hideSourceName?: string;
  onSelectSource?: SourceSelectHandler;
}

const PROJECT_DISPLAY_STATUSES = ['Backlog', 'Planning', 'In Progress', 'Review', 'Completed'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];

const displayStatusFromOccurrence = (occurrence?: TaskOccurrence | null): string => {
  const raw = occurrence?.status;
  if (!raw) return 'Backlog';
  const lower = String(raw).toLowerCase();
  if (lower === 'inprogress') return 'In Progress';
  if (lower === 'planning') return 'Planning';
  if (lower === 'review') return 'Review';
  if (lower === 'completed') return 'Completed';
  return 'Backlog';
};

const displayStatusToApiStatus = (display: string): string => {
  switch (display) {
    case 'Backlog':
      return 'OnHold';
    case 'In Progress':
      return 'InProgress';
    case 'Planning':
      return 'Planning';
    case 'Review':
      return 'Review';
    case 'Completed':
      return 'Completed';
    default:
      return 'Pending';
  }
};

const PillDropdown: React.FC<{
  value: string;
  options: string[];
  renderValue: (value: string) => React.ReactNode;
  onSelect: (nextValue: string) => void;
  minWidth?: number;
}> = ({ value, options, renderValue, onSelect, minWidth }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const handleChoose = (nextValue: string): void => {
    setOpen(false);
    if (nextValue !== value) onSelect(nextValue);
  };
  return (
    <span className="inline-select" style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-link p-0"
        ref={btnRef}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ textDecoration: 'none' }}
      >
        {renderValue(value)}
        <IoChevronDownOutline />
      </button>
      <DropdownMenuPortal
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={minWidth}
      >
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`dropdown-item${opt === value ? ' active' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              handleChoose(opt);
            }}
          >
            {opt}
          </button>
        ))}
      </DropdownMenuPortal>
    </span>
  );
};

const ProjectRow: React.FC<{
  project: MappedProject;
  employees: any[];
  onOpenOccurrence?: (occurrence: TaskOccurrence) => void;
  onArchive?: (project: MappedProject) => void;
  archiveLabel: string;
  showSource?: boolean;
  hideSourceName?: string;
  onSelectSource?: SourceSelectHandler;
}> = ({
  project,
  employees,
  onOpenOccurrence,
  onArchive,
  archiveLabel,
  showSource,
  hideSourceName,
  onSelectSource,
}) => {
  const occurrence = project.raw as TaskOccurrence;
  const [setOccurrenceStatus] = useSetOccurrenceStatusByIdMutation();
  const [updateOccurrence] = useUpdateOccurrenceMutation();

  const currentStatus = displayStatusFromOccurrence(occurrence);
  const currentPriority =
    (occurrence?.priority as string | undefined) ||
    (occurrence?.task?.priority as string | undefined) ||
    'Low';
  const dueDate = occurrence?.scheduleEnd || occurrence?.scheduleStart || null;
  const dueLabel = dueDate ? moment(dueDate).format('MM/DD/YYYY') : '-';
  const employee = employees.find((e: any) => String(e.id) === String(occurrence?.employeeId));

  const handleStatusChange = async (nextDisplay: string): Promise<void> => {
    const nextApi = displayStatusToApiStatus(nextDisplay);
    try {
      await setOccurrenceStatus({
        occurrenceId: occurrence.id,
        status: nextApi as any,
      }).unwrap();
    } catch (err) {
      apiError(err);
    }
  };

  const handlePriorityChange = async (nextPriority: string): Promise<void> => {
    try {
      await updateOccurrence({
        occurrenceId: occurrence.id,
        priority: nextPriority as any,
      }).unwrap();
    } catch (err) {
      apiError(err);
    }
  };

  const handleEmployeeSelect = async (nextId: number): Promise<void> => {
    try {
      await updateOccurrence({
        occurrenceId: occurrence.id,
        employeeId: nextId || undefined,
      }).unwrap();
    } catch (err) {
      apiError(err);
    }
  };

  const displayName = occurrence?.task?.name || occurrence?.name || project.name;

  return (
    <div className="asset-project-row">
      <div className="asset-project-row__status">
        <PillDropdown
          value={currentStatus}
          options={PROJECT_DISPLAY_STATUSES}
          renderValue={(value) => renderStatusPill(value || 'Backlog')}
          onSelect={handleStatusChange}
          minWidth={160}
        />
      </div>
      <button
        type="button"
        className="asset-project-row__name"
        title={displayName}
        onClick={() => onOpenOccurrence?.(occurrence)}
      >
        <span>{displayName}</span>
        {showSource &&
          project.source?.name &&
          (!hideSourceName || project.source.name !== hideSourceName) &&
          (() => {
            const src = project.source!;
            const kindLabel = src.kind === 'group' ? 'Group' : 'Item';
            const clickable = typeof onSelectSource === 'function';
            const handleActivate = (): void => {
              if (!clickable) return;
              onSelectSource?.({ id: src.id, kind: src.kind });
            };
            return (
              <span
                className={`asset-section__source-chip asset-section__source-chip--${src.kind}${
                  clickable ? ' asset-section__source-chip--clickable' : ''
                }`}
                title={`${kindLabel}: ${src.name}`}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={
                  clickable
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleActivate();
                      }
                    : undefined
                }
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          handleActivate();
                        }
                      }
                    : undefined
                }
              >
                <span className="asset-section__source-chip-kind">{kindLabel}</span>
                <span className="asset-section__source-chip-name">{src.name}</span>
              </span>
            );
          })()}
      </button>
      <div className="asset-project-row__due">{dueLabel}</div>
      <div className="asset-project-row__assignee">
        <EmployeeInitialsDropdown
          selectable
          selectedEmployee={occurrence?.employeeId ?? 0}
          onEmployeeSelect={handleEmployeeSelect}
          listHeight={150}
        />
        <span className="asset-project-row__assignee-label">
          {employee ? `${employee.lastName}, ${employee.firstName}` : 'Unassigned'}
        </span>
      </div>
      <div className="asset-project-row__priority">
        <PillDropdown
          value={currentPriority}
          options={PRIORITY_OPTIONS}
          renderValue={(value) => renderPriorityPill(value || 'Low')}
          onSelect={handlePriorityChange}
          minWidth={140}
        />
      </div>
      {onArchive && (
        <button
          type="button"
          className="asset-section__row-archive"
          title={archiveLabel}
          onClick={() => onArchive(project)}
        >
          <i className="uil uil-archive" />
        </button>
      )}
    </div>
  );
};

const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  projects,
  onAdd,
  onOpenOccurrence,
  onArchive,
  showSource,
  hideSourceName,
  onSelectSource,
}) => {
  const intl = useIntl();
  const employees = useSelector(selectEmployees) as any[];

  const rows = useMemo(() => projects.filter((p) => !!p.raw), [projects]);
  const archiveLabel = intl.formatMessage({ id: 'archive', defaultMessage: 'Archive' });

  return (
    <div className="asset-section asset-section--projects">
      <div className="asset-section__header">
        <div className="asset-section__header-left">
          <FaRegCalendarDays className="asset-section__header-icon" size={16} color="#6c757d" />
          <h3 className="asset-section__title">
            {intl.formatMessage({ id: 'assets.sections.projects' })}
          </h3>
          <Badge color="secondary" pill>
            {rows.length}
          </Badge>
        </div>
        <Button outline color="primary" size="sm" onClick={onAdd}>
          <i className="uil uil-plus mr-1" />
          {intl.formatMessage({ id: 'assets.actions.add' })}
        </Button>
      </div>

      <div className="asset-section__content">
        {rows.length === 0 ? (
          <div className="asset-section__empty">
            <div className="asset-section__empty-icon">
              <i className="uil uil-clipboard-notes" />
            </div>
            <p className="asset-section__empty-title">
              {intl.formatMessage({ id: 'assets.projects.empty' })}
            </p>
            <p className="asset-section__empty-description">
              {intl.formatMessage({ id: 'assets.projects.emptyDescription' })}
            </p>
            <Button color="primary" size="sm" onClick={onAdd}>
              <i className="uil uil-plus mr-1" />
              {intl.formatMessage({ id: 'assets.projects.addProject' })}
            </Button>
          </div>
        ) : (
          <div className="asset-project-table">
            <div className="asset-project-table__header">
              <span>{intl.formatMessage({ id: 'status', defaultMessage: 'Status' })}</span>
              <span>{intl.formatMessage({ id: 'name', defaultMessage: 'Name' })}</span>
              <span>{intl.formatMessage({ id: 'dueDate', defaultMessage: 'Due Date' })}</span>
              <span>{intl.formatMessage({ id: 'assignee', defaultMessage: 'Assignee' })}</span>
              <span>{intl.formatMessage({ id: 'priority', defaultMessage: 'Priority' })}</span>
              <span />
            </div>
            {rows.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                employees={employees || []}
                onOpenOccurrence={onOpenOccurrence}
                onArchive={onArchive}
                archiveLabel={archiveLabel}
                showSource={showSource}
                hideSourceName={hideSourceName}
                onSelectSource={onSelectSource}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsSection;
